import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { TextToSpeechService, TtsStatusRow } from 'src/app/services/text-to-speech.service';

const FAST_INTERVAL_MS = 3000;
const SLOW_INTERVAL_MS = 10000;
const FAST_WINDOW_MS = 30000;

const MAX_POLL_MS = 180000;

// Provided on the editor component, never in root, so the poller dies with the
// form and two editors cannot share a map.
@Injectable()
export class TtsStatusService implements OnDestroy {
  private rows: Record<string, TtsStatusRow> = {};

  private entityId: string | null = null;

  private timer: any = null;
  private pollStartedAt = 0;
  private request: Subscription | null = null;

  timedOut = false;
  // False until a status load succeeds: a field with no row only means "not
  // generated" once content-api has answered, not while the call is failing.
  loaded = false;

  private readonly onVisibilityChange = () => {
    if (typeof document === 'undefined') {
      return;
    }
    if (document.hidden) {
      this.clearTimer();
    } else if (this.hasPending() && !this.timedOut) {
      this.tick();
    }
  };

  constructor(private textToSpeech: TextToSpeechService) {
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.onVisibilityChange);
    }
  }

  load(entityId: string): Observable<TtsStatusRow[]> {
    this.entityId = entityId;
    this.loaded = false;

    return this.textToSpeech.status(entityId).pipe(
      map((rows: TtsStatusRow[]) => {
        this.rows = {};
        this.loaded = true;
        (rows || []).forEach(row => {
          this.rows[row.fieldPath] = row;
        });
        return rows || [];
      })
    );
  }

  get(fieldPath: string): TtsStatusRow | null {
    return this.rows[fieldPath] ?? null;
  }

  hasPending(): boolean {
    return Object.values(this.rows).some(row => row.status === 'pending');
  }

  // A `ready` row only moves on a save or a regenerate, and both re-seed the
  // map — so a field enqueued in another tab is missed until the next load.
  private notReadyFieldPaths(): string[] {
    return Object.values(this.rows)
      .filter(row => row.status !== 'ready')
      .map(row => row.fieldPath);
  }

  hasAnyRows(): boolean {
    return Object.keys(this.rows).length > 0;
  }

  startPolling(): void {
    if (!this.hasPending() || this.timer) {
      return;
    }
    this.timedOut = false;
    this.pollStartedAt = Date.now();
    this.schedule();
  }

  stop(): void {
    this.clearTimer();
    if (this.request) {
      this.request.unsubscribe();
      this.request = null;
    }
  }

  regenerate(fieldPath: string, text: string, force: boolean): Observable<TtsStatusRow[]> {
    if (!this.entityId) {
      throw new Error('TtsStatusService.regenerate called before load()');
    }

    return this.textToSpeech.regenerate(this.entityId, fieldPath, text, force).pipe(
      map((rows: TtsStatusRow[]) => {
        (rows || []).forEach(row => {
          this.rows[row.fieldPath] = row;
        });
        this.timedOut = false;
        this.startPolling();
        return rows || [];
      })
    );
  }

  ngOnDestroy(): void {
    this.stop();
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }
  }

  private schedule(): void {
    const elapsed = Date.now() - this.pollStartedAt;
    const interval = elapsed < FAST_WINDOW_MS ? FAST_INTERVAL_MS : SLOW_INTERVAL_MS;
    this.timer = setTimeout(() => this.tick(), interval);
  }

  private tick(): void {
    this.clearTimer();

    if (!this.entityId || !this.hasPending()) {
      return;
    }

    if (Date.now() - this.pollStartedAt >= MAX_POLL_MS) {
      this.timedOut = true;
      return;
    }

    // Narrowed request: rows are merged, so the `ready` fields left out of it
    // keep their state instead of dropping out of the map.
    this.request = this.textToSpeech.status(this.entityId, this.notReadyFieldPaths()).subscribe({
      next: (rows: TtsStatusRow[]) => {
        (rows || []).forEach(row => {
          this.rows[row.fieldPath] = row;
        });
        if (this.hasPending()) {
          this.schedule();
        }
      },
      // A failed tick is not surfaced; the next one corrects it.
      error: () => this.schedule(),
    });
  }

  private clearTimer(): void {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }
}
